/* ============================================================
   Partpoint — Dark Mode JS v2 — 2026-05-26
   Changes vs v1:
   - Default is LIGHT, not system preference
   - No longer follows OS-level prefers-color-scheme changes
   - User must explicitly click the toggle to switch to dark
   ============================================================ */

(function () {
  'use strict';

  var STORAGE_KEY = 'partpoint-theme';
  var root = document.documentElement;

  /* ---------- Helpers ---------- */
  function getStored() {
    try {
      var v = localStorage.getItem(STORAGE_KEY);
      return (v === 'dark' || v === 'light') ? v : null;
    } catch (e) { return null; }
  }

  function setStored(theme) {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch (e) {}
  }

  function resolveInitial() {
    // v2: light by default; only respect explicit stored choice
    return getStored() || 'light';
  }

  /* ---------- Apply theme ---------- */
  function applyTheme(theme) {
    root.setAttribute('data-theme', theme);

    // Update <meta name="theme-color"> (mobile browser chrome)
    var meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', theme === 'dark' ? '#0b0c0e' : '#ffffff');

    // Sync all toggle buttons on the page
    var buttons = document.querySelectorAll('.theme-toggle');
    for (var i = 0; i < buttons.length; i++) {
      var actionLabel = theme === 'dark' ? buttons[i].dataset.labelLight : buttons[i].dataset.labelDark;
      if (actionLabel) {
        buttons[i].setAttribute('aria-label', actionLabel);
        buttons[i].setAttribute('title', actionLabel);
      }
      buttons[i].setAttribute('aria-pressed', theme === 'dark' ? 'true' : 'false');
      buttons[i].setAttribute('data-theme-current', theme);
    }

    // Custom event for other scripts
    try {
      window.dispatchEvent(new CustomEvent('partpoint:theme-change', { detail: { theme: theme } }));
    } catch (e) {}
  }

  /* ---------- Toggle with View Transitions ---------- */
  function toggleTheme(originEvent) {
    var current = root.getAttribute('data-theme') || resolveInitial();
    var next = current === 'dark' ? 'light' : 'dark';

    setStored(next);

    var reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (document.startViewTransition && !reducedMotion) {
      var x = (originEvent && typeof originEvent.clientX === 'number')
        ? originEvent.clientX
        : (window.innerWidth - 50);
      var y = (originEvent && typeof originEvent.clientY === 'number')
        ? originEvent.clientY
        : 50;

      var transition = document.startViewTransition(function () { applyTheme(next); });

      transition.ready.then(function () {
        var endRadius = Math.hypot(
          Math.max(x, window.innerWidth - x),
          Math.max(y, window.innerHeight - y)
        );
        document.documentElement.animate(
          {
            clipPath: [
              'circle(0px at ' + x + 'px ' + y + 'px)',
              'circle(' + endRadius + 'px at ' + x + 'px ' + y + 'px)'
            ]
          },
          {
            duration: 450,
            easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
            pseudoElement: '::view-transition-new(root)'
          }
        );
      }).catch(function () { /* graceful */ });
    } else {
      applyTheme(next);
    }
  }

  /* ---------- Boot ---------- */
  function boot() {
    // Allow transitions only after initial paint
    root.classList.add('pp-theme-ready');

    // Make sure theme is applied (inline head script should already have done this)
    if (!root.getAttribute('data-theme')) {
      applyTheme(resolveInitial());
    } else {
      applyTheme(root.getAttribute('data-theme'));
    }

    // Delegated click — works for all current and future .theme-toggle buttons
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('.theme-toggle');
      if (btn) {
        e.preventDefault();
        toggleTheme(e);
      }
    }, false);

    // v2: NO OS prefers-color-scheme listener — explicit user choice only.

    // Sync across tabs / windows
    window.addEventListener('storage', function (e) {
      if (e.key === STORAGE_KEY && (e.newValue === 'dark' || e.newValue === 'light')) {
        applyTheme(e.newValue);
      }
    });

    // Re-attach to dynamically added toggles (Shopify section reloads in theme editor)
    document.addEventListener('shopify:section:load', function () {
      applyTheme(root.getAttribute('data-theme') || resolveInitial());
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Public mini-API (for debugging / programmatic control)
  window.PartpointTheme = {
    get: function () { return root.getAttribute('data-theme') || 'light'; },
    set: function (t) {
      if (t === 'dark' || t === 'light') {
        setStored(t);
        applyTheme(t);
      }
    },
    toggle: function () { toggleTheme(); },
    clear: function () {
      try { localStorage.removeItem(STORAGE_KEY); } catch (e) {}
      applyTheme('light'); // v2: default back to light, not system
    }
  };
})();
